import { Text } from '@understory-io/leaf/text';
import { css } from '@understory-io/leaf/css';
import { FormattedMessage, useIntl } from 'react-intl';
import { StarIcon } from '~/ui/icons/star';

export type RatingBadgeProps = {
  /** The unrounded average the service returns; shown to one decimal. */
  average: number;
  /** Number of rated reviews behind the average. Never zero — a company with
   *  no ratings shows no badge at all, so the caller omits it. */
  count: number;
};

/**
 * The company's rating, as a star, the average, and the number of reviews
 * behind it.
 *
 * The star takes the storefront's accent — the Leaf provider rewrites
 * `--colors-interactive-accent-*` from the company's primaryColor — so the
 * badge is branded the same way the review form's stars are.
 */
export function RatingBadge({ average, count }: RatingBadgeProps) {
  const intl = useIntl();

  return (
    <div className={badgeStyles}>
      <span className={ratingStyles}>
        <StarIcon size="large-inset" color="interactive.accent.default" />
        <Text style="body-large-bold">
          {intl.formatNumber(average, {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          })}
        </Text>
      </span>
      <Text style="body-large-regular" color="secondary">
        <FormattedMessage
          defaultMessage="({count, plural, one {# review} other {# reviews}})"
          description="Rating badge: how many reviews the average next to it is drawn from"
          values={{ count }}
        />
      </Text>
    </div>
  );
}

const badgeStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'tiny',
});

const ratingStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'micro',
});
